import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  TrendingUp,
  ArrowRight,
  Check,
  Star,
  Filter,
  Globe,
  Database,
  Bell,
  Download,
  ChevronRight,
  Sparkles,
  Shield,
  Zap,
  BarChart3
} from 'lucide-react';
import { markets, stats, features, testimonials, pricingPlans } from '../data/mockData';

const LandingPage = () => {
  const iconMap = {
    Filter: Filter,
    Globe: Globe,
    Database: Database,
    Bell: Bell,
    Star: Star,
    Download: Download,
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-emerald-400 font-medium">
                Vietnam FTSE Upgrade Septembre 2026
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Découvrez les Small-Caps{' '}
              <span className="gradient-text">sous-valorisées</span>
              {' '}des pays "<span className="gradient-text">émergents</span>" en pleine croissance
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-gray-400 mb-8 max-w-3xl mx-auto">
              VALORAMAP est le premier screener francophone donnant accès aux marchés 
              émergents explosifs : <span className="text-emerald-400">Vietnam</span>, <span className="text-emerald-400">Inde</span>, <span className="text-emerald-400">Philippines</span>, <span className="text-emerald-400">Indonésie</span>, <span className="text-emerald-400">Pologne</span> et <span className="text-emerald-400">Corée du Sud</span>. 
              <span className="text-white font-medium"> 6 200+ actions sous-valorisées</span>. 
              Inaccessible ailleurs en français.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Link to="/pricing">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 h-12 text-base">
                  Essai 7 jours — 9 CHF
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/screener">
                <Button variant="outline" size="lg" className="border-white/20 text-white hover:bg-white/5 px-8 h-12 text-base">
                  Voir une démo
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span>Données sécurisées</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-emerald-400" />
                <span>Mise à jour quotidienne</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-400" />
                <span>Annulation à tout moment</span>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="text-center p-6 rounded-2xl bg-white/5 border border-white/10"
              >
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Markets Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#080c14]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              6 Marchés Émergents, Une Seule Plateforme
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Accédez aux économies avec la croissance la plus rapide au monde. 
              Des opportunités inaccessibles avec les screeners traditionnels.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {markets.map((market) => (
              <Card 
                key={market.code} 
                className="bg-white/5 border-white/10 hover:border-emerald-500/30 transition-all card-hover cursor-pointer"
              >
                <CardContent className="p-5 text-center">
                  <div className="text-3xl mb-2">{market.flag}</div>
                  <h3 className="font-semibold text-white mb-1">{market.name}</h3>
                  <div className="text-emerald-400 font-bold text-lg mb-1">
                    {market.gdpGrowth}
                  </div>
                  <div className="text-xs text-gray-500">Croissance GDP</div>
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <span className="text-sm text-gray-400">
                      {market.stocks.toLocaleString()} actions
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Vietnam Highlight */}
          <div className="mt-12 p-6 sm:p-8 rounded-2xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <Badge className="bg-emerald-500/20 text-emerald-400 border-0 mb-3">
                  Opportunité 2026
                </Badge>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                  Vietnam : Upgrade FTSE Russell en Septembre 2026
                </h3>
                <p className="text-gray-400">
                  Le Vietnam passera de Frontier à Secondary Emerging Market. 
                  Inflows attendus : $3-5 milliards de fonds indiciels. 
                  Positionnez-vous avant les institutionnels.
                </p>
              </div>
              <Link to="/learn/vietnam-upgrade">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white whitespace-nowrap">
                  En savoir plus
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Tout ce qu'il faut pour dénicher des pépites
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Des outils professionnels conçus pour les investisseurs value 
              qui veulent accéder aux marchés émergents.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = iconMap[feature.icon] || BarChart3;
              return (
                <Card 
                  key={index} 
                  className="bg-white/5 border-white/10 hover:border-emerald-500/30 transition-all"
                >
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#080c14]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ce que disent nos utilisateurs
            </h2>
            <p className="text-gray-400">
              Rejoignez les investisseurs qui ont déjà adopté VALORAMAP.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <Card 
                key={testimonial.id} 
                className="bg-white/5 border-white/10"
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-emerald-400 text-emerald-400" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-4 text-sm leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-medium">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <div className="font-medium text-white text-sm">
                        {testimonial.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {testimonial.role}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Un prix simple, une valeur immense
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Commencez avec un essai à 9 CHF, puis choisissez le plan qui vous convient.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingPlans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`relative bg-white/5 border-white/10 ${
                  plan.popular ? 'border-emerald-500/50 ring-1 ring-emerald-500/20' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-emerald-500 text-white border-0">
                      Le plus populaire
                    </Badge>
                  </div>
                )}
                {plan.limited && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-500 text-white border-0">
                      {plan.remaining} places restantes
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6 pt-8">
                  <h3 className="text-lg font-semibold text-white mb-1">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">{plan.description}</p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    <span className="text-gray-300 ml-1">CHF</span>
                    <span className="text-gray-400 ml-1">/{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/pricing">
                    <Button 
                      className={`w-full ${
                        plan.popular 
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
                          : 'bg-white/10 hover:bg-white/15 text-white'
                      }`}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#080c14]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Prêt à découvrir les opportunités émergentes ?
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Rejoignez les investisseurs qui ont déjà accès aux small-caps 
            les plus prometteuses du Vietnam, de l'Inde et de l'ASEAN.
          </p>
          <Link to="/pricing">
            <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 h-12 text-base">
              Commencer l'essai à 9 CHF
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <p className="mt-4 text-sm text-gray-500">
            Remboursé si non satisfait. Pas d'engagement.
          </p>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
